import React, { FC, useEffect, useState } from "react";
import { setCookiesConsent } from "@/utils/cookieConsent";
import { getCookie } from "@/utils/cookies";
import { StorageName } from "@/constants/storageName";
import { Checkbox } from "@/components/basic/Checkbox";
import { useCheckboxes } from "@/hooks/useCheckboxes";
import { isSSR } from "@/utils/isSSR";

const FirstStep: React.FC<{
  onApprove: () => void;
  onReject: () => void;
  showMore: () => void;
}> = ({ onApprove, onReject, showMore }) => (
  <>
    <div id="first-step-info">
      <div className="flex-box flex-box-space-between flex-align-center">
        <b className="cookies-title">Use of cookies</b>
        <div className="anixe-logo-frame">
          <img
            className="anixe-logo centred-icon"
            alt="ANIXE logo"
            src="/images/logo_white.png"
          />
        </div>
      </div>
      <div className="decor-line"></div>
      <div>
        <p>
          With your consent, we use services on our portal to provide you with
          service functions and content from external media and to record and
          optimise the performance of marketing measures. These services will
          only be used if you give your consent by clicking on “Agree and
          Proceed”. You can also use our website without consent by clicking on
          "More Info" and “save and close” while all preferences are set to
          “off”.
        </p>
        <p>
          You can revoke your consent at any time with effect for the future at
          the bottom of the page under "Cookie settings". Further information is
          available under “more “info and on our data protection page.
        </p>
        <p>
          The services use cookies, process personal data such as IP addresses,
          store and retrieve information on your end device, collect visit and
          product data and create visit profiles.
        </p>
        <p>
          Individual services may transfer data to a country outside the EU for
          which there is no adequacy decision by the Commission. If a data
          transfer takes place outside the scope of the EU GDPR, this is
          secured, among other things, with EU standard contractual clauses in
          accordance with Art. 46 para. 2 lit. c EU GDPR.
        </p>
        <p>
          Where necessary and possible, the EU standard contractual clauses are
          supplemented by further contractual assurances. There is nevertheless
          a risk that state authorities may access this data for control and
          monitoring purposes and that no effective legal remedies are available
          against this.
        </p>
        <p>
          In all other cases, the transfer of data to a recipient in an insecure
          third country is legitimised on the basis of your consent under Art.
          49 para. 1 lit. a GDPR.
        </p>
        {/*<p>*/}
        {/*  We (ANIXE Polska) would like to set optional analytics and retargeting*/}
        {/*  cookies on your device to help us improve our website (based on*/}
        {/*  reports and statistics about how you use our website) and improve our*/}
        {/*  marketing activities (based on tracking your visits on our website).*/}
        {/*  The cookies we use are provided by Google LLC seated in USA. The*/}
        {/*  information that you have visited this website may be shared with*/}
        {/*  third-party vendors, including Google.*/}
        {/*</p>*/}
        {/*<p>*/}
        {/*  Do you agree? Your consent is voluntary and may be withdrawn at any*/}
        {/*  time with the effect for the future. Click on „More Info” button to*/}
        {/*  choose your own cookies’ settings and learn more.*/}
        {/*</p>*/}
      </div>
    </div>
    <div className="first-step-buttons flex-box flex-box-space-between flex-align-center">
      <div className="close-elements-in-row">
        <div className="l-push-after">
          <a
            onClick={onApprove}
            aria-label="accept all cookies and proceed"
            tabIndex={0}
            className="cc-btn"
          >
            AGREE AND PROCEED
          </a>
        </div>
        <div>
          <a
            onClick={onReject}
            aria-label="reject all cookies"
            tabIndex={0}
            className="cc-btn l-push-bottom"
          >
            REJECT ALL
          </a>
        </div>
      </div>
      <a
        onClick={showMore}
        aria-label="show cookie preferences"
        tabIndex={0}
        className="more-info-link"
      >
        More info
      </a>
    </div>
  </>
);

interface SecondStepProps {
  onApprove: (allowAnalytics: boolean, allowRetargetting: boolean) => void;
  allowAnalytics?: boolean;
  allowRetargetting?: boolean;
}
export const SecondStep: FC<SecondStepProps> = ({
  onApprove,
  allowAnalytics,
  allowRetargetting,
}) => {
  const [checkboxesState, changeCheckboxState] = useCheckboxes({
    analyticsCheckbox: allowAnalytics ?? false,
    retargettingCheckbox: allowRetargetting ?? false,
  });

  return (
    <>
      <div id="second-step-info">
        <div className="flex-box flex-box-space-between flex-align-center">
          <b className="cookies-title">Cookie preferences</b>
          <div className="anixe-logo-frame">
            <img
              className="anixe-logo centred-icon"
              alt="ANIXE logo"
              src="/images/logo_white.png"
            />
          </div>
        </div>
        <div className="decor-line"></div>
        <div className="flex-grid-20-80">
          <div>
            <Checkbox
              name="analyticsCheckbox"
              checked={checkboxesState["analyticsCheckbox"]}
              callback={() => changeCheckboxState("analyticsCheckbox")}
            />
          </div>
          <div className="l-with-half-gutter-bottom-mobile l-10-cols-tablet l-12-cols-mobile">
            <b>Analytics cookies</b>
            <p>
              We (ANIXE Polska) would like to set Google Analytics cookies on
              your device to help us to improve our website by collecting and
              reporting information on how you use it. The cookies collect
              information in a way that does not directly identify anyone. The
              period of activity of these cookies is up to 2 years.
            </p>
          </div>
        </div>
        <div className="flex-grid-20-80">
          <div>
            <Checkbox
              name="retargettingCheckbox"
              checked={checkboxesState["retargettingCheckbox"]}
              callback={() => changeCheckboxState("retargettingCheckbox")}
            />
          </div>
          <div className="l-with-half-gutter-bottom-mobile l-10-cols-tablet l-12-cols-mobile">
            <b>Retargeting cookies</b>
            <p>
              We (ANIXE Polska) would like to set Google Ads cookies on your
              device to help us to improve our marketing activities. These
              cookies are used to display ads better tailored to your interests.
              These cookies remember that you visited our website and share this
              information with third-party vendors, including Google to serve
              you ads based on your past visits to our website. These are
              session cookies. Web browsers normally delete session cookies when
              the user closes the browser.
            </p>
          </div>
        </div>
      </div>
      <div
        id="second-step-buttons"
        className="close-elements-in-row l-with-dbl-gutter-top-mobile"
      >
        <div className="l-push-after">
          <a
            onClick={() => onApprove(true, true)}
            aria-label="accept all cookies"
            tabIndex={0}
            className="cc-btn"
          >
            ACCEPT ALL
          </a>
        </div>
        <div>
          <a
            onClick={() =>
              onApprove(
                checkboxesState["analyticsCheckbox"],
                checkboxesState["retargettingCheckbox"]
              )
            }
            aria-label="save cookie preferences and close"
            tabIndex={0}
            className="cc-btn l-push-bottom"
          >
            Save and close
          </a>
        </div>
      </div>
    </>
  );
};

export interface CookieConsentProps {
  showSecondStep: boolean;
  onClose: () => void;
}

export const CookieConsent: FC<CookieConsentProps> = ({
  showSecondStep,
  onClose,
}) => {
  const [showCookiesBanner, setShowCookiesBanner] = useState(false);
  const [showMore, setShowMore] = useState(false);
  useEffect(() => {
    if (!getCookie(StorageName.cookieConsentStatus)) {
      setShowCookiesBanner(true);
    }
    document.body.classList.toggle("cookies", showCookiesBanner);
  }, [showCookiesBanner]);

  const approveCallback = (showAnalyticsCookies, showRetargettingCookies) => {
    setCookiesConsent(showAnalyticsCookies, showRetargettingCookies);
    setShowCookiesBanner(false);
    // activateGoogleScripts();
    onClose();
  };

  const allowAnalytics =
    !isSSR() &&
    localStorage.getItem(StorageName.analytics) &&
    localStorage.getItem(StorageName.analytics) === "true";

  const allowRetargetting =
    !isSSR() &&
    localStorage.getItem(StorageName.retargeting) &&
    localStorage.getItem(StorageName.retargeting) === "true";

  return (
    (showCookiesBanner || showSecondStep) && (
      <div className="fixed-action z-overlay">
        <div id="cookie-consent">
          <div
            role="dialog"
            aria-live="polite"
            aria-label="cookieconsent"
            aria-describedby="cookieconsent:desc"
            className="cc-window cc-banner cc-type-info cc-theme-block cc-bottom cc-color-override--1351869031 "
          >
            <div className="display-column l-with-dbl-side-gutters-mobile-cookie-consent">
              <div className="scrollable-text" id="scrollableDiv">
                {showMore || showSecondStep ? (
                  <SecondStep
                    onApprove={approveCallback}
                    allowAnalytics={allowAnalytics}
                    allowRetargetting={allowRetargetting}
                  />
                ) : (
                  <FirstStep
                    onApprove={() => approveCallback(true, true)}
                    onReject={() => approveCallback(false, false)}
                    showMore={() => setShowMore(true)}
                  />
                )}
                <p>
                  For more information about the cookies we use please see also
                  our&nbsp;
                  <b>
                    <a
                      aria-label="learn more about cookies"
                      tabIndex={0}
                      className="cookie-consent-link"
                      href="https://anixe.pl/privacy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Data Protection Declaration
                    </a>
                  </b>
                  . To see our <b>Terms of Service</b> click&nbsp;
                  <b>
                    <a
                      aria-label="learn more about cookies"
                      tabIndex={0}
                      className="cookie-consent-link"
                      href="https://anixe.pl/terms-of-service"
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </b>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};
